// useModalA11y — a11y homogène pour les modales du cockpit.
//
// Dette rattrapée (da-guard) : les modales du Suivi n'avaient ni fermeture
// Escape ni focus initial. Ce hook apporte les deux, de façon non bloquante et
// sans changer le comportement existant (clic hors-modale, boutons, etc.).
//
//   (a) Escape → appelle onClose, SEULEMENT si pas en cours d'enregistrement
//       (busy=false). Pendant une sauvegarde, Escape est ignoré comme les
//       autres portes de fermeture (clic overlay, bouton) le sont déjà.
//   (b) Au montage, place le focus sur le premier élément actionnable de la
//       modale (ou l'élément ciblé par initialFocusRef s'il est fourni).
//
// Nettoyage du listener au démontage.
//
// @param {object}   params
// @param {() => void} params.onClose        - fermeture de la modale
// @param {boolean}  [params.busy=false]      - true pendant une sauvegarde (Escape ignoré)
// @param {import('react').RefObject} params.panelRef        - ref du conteneur de la modale (focus initial)
// @param {import('react').RefObject} [params.initialFocusRef] - ref d'un élément à focus en priorité
// @returns {void}

import { useEffect } from 'react';

const FOCUSABLE_SELECTOR = [
  'button:not([disabled])',
  'a[href]',
  'input:not([disabled])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(',');

export function useModalA11y({ onClose, busy = false, panelRef, initialFocusRef } = {}) {
  // Escape → onClose (sauf pendant un enregistrement). Ré-attaché quand busy ou
  // onClose changent pour toujours refléter l'état courant.
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key !== 'Escape') return;
      if (busy) return;
      if (typeof onClose === 'function') {
        e.stopPropagation();
        onClose();
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [onClose, busy]);

  // Focus initial au montage uniquement (pas de re-focus intempestif à chaque
  // rendu). Priorité à initialFocusRef, sinon premier actionnable du panel.
  useEffect(() => {
    const target =
      initialFocusRef?.current ||
      panelRef?.current?.querySelector(FOCUSABLE_SELECTOR) ||
      panelRef?.current;
    if (target && typeof target.focus === 'function') {
      // focus sans scroll brusque quand le navigateur le supporte.
      try {
        target.focus({ preventScroll: true });
      } catch {
        target.focus();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
}
